import {
    Component,
    ContentChild,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output,
    Renderer2,
    TemplateRef,
    ChangeDetectorRef,
    ViewChild,
    ElementRef
} from '@angular/core';
import { trigger, style, animate, transition, query, AnimationEvent, sequence } from '@angular/animations';
import { Router } from '@angular/router';
import { Title } from '@angular/platform-browser';

import { Observable, Subscription } from 'rxjs';
import { DomHandler } from 'primeng/dom';

import { ActionKeys } from '../../../Forms/enums/keys.enum';
import { ButtonTypesEnum } from '../../../Forms/enums/button-types.enum';
import { CheckboxTypesEnum } from '../../../Forms/enums/checkbox-types.enum';
import { FullProfileAnimationModel } from '../../../../Core/Dialogs/models/full-profile-animation.model';
import { FullProfileDialogAnimationsEnum } from '../../../../Core/Dialogs/enums/full-profile-dialog-animations.enum';
import { LocalizationService } from '../../../../Core/Localization/services/localization.service';
import { messageSeverityEnum } from '../../../Misc/enums/message-severity.enum';
import { OverlayAnimationStateEnum } from '../../../../Core/Dialogs/enums/overlay-animation.enum';

export class SplitHeader {
    icon: string;
    initialText: string;
    value: string;
    endText: string;
    loading: boolean;
}

@Component({
    selector: 'agpr-take-over-panel',
    templateUrl: './take-over-panel.component.html',
    styleUrls: ['./take-over-panel.component.scss'],
    animations: [
        trigger(FullProfileDialogAnimationsEnum.slideInOutAnimation, [
            transition('* => open', [
                query(':self', [
                    style({ position: 'fixed', height: 'calc(100% - {{navBarHeight}})', left: '100%', opacity: 0 }),
                    sequence([
                        animate('0.3s ease-out', style({ left: '{{left}}%', opacity: 1 }))
                    ])
                ]),
            ]),
            transition('* => close', [
                query(':self', [
                    style({ position: 'fixed', height: 'calc(100% - {{navBarHeight}})', left: '{{left}}%', opacity: 1 }),
                    sequence([
                        animate('0.3s ease-in', style({ left: '100%', opacity: 0 })),
                    ])
                ])
            ])
        ]),
        trigger(FullProfileDialogAnimationsEnum.overlayAnimation, [
            transition('* => open', [
                style({ opacity: 0 }),
                sequence([
                    animate('0.3s', style({ opacity: 1 })),
                ])
            ]),
            transition('* => close', [
                style({ opacity: 1 }),
                sequence([
                    animate('0.3s 0.15s', style({ opacity: 0 })),
                ])
            ])
        ])
    ],
    standalone: false
})
export class TakeOverPanelComponent implements OnInit, OnDestroy {

    @Input() id: string;
    @Input() overlayContainer: Element;
    @Input() header: string;
    @Input() subHeader: string;
    @Input() splitHeader: SplitHeader;
    @Input() headerIcon: string;
    // required if no header is provided
    @Input() invisibleHeader: string;
    @Input() footerContext: string;
    @Input() animationTrigger: Observable<FullProfileAnimationModel>;
    @Input() set hideSlideOverlay(hide: boolean) {
        if (hide) {
            this.close();
        }
    }
    @Input() styleClass: string;
    // Width Options
    @Input() width: number; // Fixed percentage width
    @Input() fullWidth: boolean = false;
    @Input() responsiveWidth: boolean = false; // Variable percentage width - must provide max and min panel values
    // Responsive Width Options
    @Input() minPanelWidth: number;
    @Input() maxPanelWidth: number;

    @Input() fullHeight: boolean = false;
    @Input() focusCloseButton: boolean = true;
    @Input() backToButtonString: string;
    @Input() arrowBackBar: string;
    @Input() showFooter: boolean = true;
    @Input() hasSideTabs: boolean = false;
    @Input() showTopPadding: boolean = true;
    @Input() coverNav: boolean = true;
    @Input() whiteBg: boolean = false;
    @Input() validateBeforeClose: Observable<boolean>;
    @Input() title: string;
    @Input() showProgressBar: boolean = false;
    @Input() used: number = 0;
    @Input() total: number = 0;
    @Input() ariaLabel: string;
    @Input() noScroll: boolean = false;
    @Input() noPadding: boolean = false;

    @ViewChild('takeOverPanelHeader') takeOverPanelHeader: ElementRef;
    /** Rendered inside the header, before the icon and title - eg. a collapse control */
    @ContentChild('takeOverHeaderStart') takeOverHeaderStart: TemplateRef<any>;
    @ContentChild('takeOverFooterLeft') takeOverFooterLeft: TemplateRef<any>;
    @ContentChild('takeOverFooterRight') takeOverFooterRight: TemplateRef<any>;
    @ContentChild('noScrollContent') noScrollContent: TemplateRef<any>;


    @Output() visibleChange: EventEmitter<boolean> = new EventEmitter<boolean>();
    @Output() closeTakeover: EventEmitter<boolean> = new EventEmitter<boolean>();
    @Output() finishedOpen: EventEmitter<boolean> = new EventEmitter<boolean>();
    @Output() progressBarMouseEnter: EventEmitter<any> = new EventEmitter<any>();

    readonly checkboxTypesEnum = CheckboxTypesEnum;
    readonly buttonTypes = ButtonTypesEnum;
    readonly messageSeverityEnum = messageSeverityEnum;
    navBar: Element;
    // browser.mjs:592:25 gives null error without initial value here -- NEP-11652
    navBarHeight: string = '0px';
    panelHeight: string;
    readonly footerHeight = 47;
    readonly headerHeight = 45;
    readonly socialPadding = 40;
    readonly minWindowWidth = 1000;
    readonly maxWindowWidth = 1920;

    overlay: Element;

    // right is needed in the BMIE, do not remove
    slideInOutAnimation: {value: OverlayAnimationStateEnum; params: {left?: number; right?: number; navBarHeight: string}};
    overlayAnimation: OverlayAnimationStateEnum;
    zIndex: number;
    overlayClassName: string = 'agpr-takeOverPanel-overlay';
    clickOutsideExcluded: string = '.agpr-clickOutsideExcluded';
    done: boolean = true;
    oldTitle: string;
    isPanelFullyOpened: boolean = false;
    private subscriptions: Subscription[] = [];

    navHeightObserver = new ResizeObserver((entry) => {
        const navHeight = entry[0].contentRect.height;
        this.calculatePosition(navHeight);
    });

    get progressValue(): number {
        if (this.showProgressBar && !!this.used && !!this.total) {
            return (this.used / this.total) * 100;
        }
        return 0;
    }

    constructor(
        protected renderer: Renderer2,
        public localizationService: LocalizationService,
        public router: Router,
        public cd: ChangeDetectorRef,
        protected titleService: Title
    ) {
        this.zIndex = ++DomHandler.zindex;
    }

    private windowResizeHandler = () => {
        if (!this.coverNav) {
            this.calculatePosition(this.navBar.clientHeight);
        }
        if (this.responsiveWidth) {
            this.calculatePanelWidth();
        }
    };

    ngOnInit() {
        if (this.animationTrigger) {
            this.subscriptions.push(
                this.animationTrigger.subscribe(animationModel => {
                    if (animationModel.animation === FullProfileDialogAnimationsEnum.slideInOutAnimation) {
                        this.overlayAnimationStart(<AnimationEvent> {
                            fromState: null,
                            toState: OverlayAnimationStateEnum.open
                        });
                    }
                })
            );
        }
        if (this.responsiveWidth) {
            this.calculatePanelWidth();
        }
        if (!this.coverNav) {
            this.navBar = document.querySelector('.agpr-masterLayout-navigation');
            this.navHeightObserver.observe(this.navBar);
            this.calculatePosition(this.navBar.clientHeight);
        } else {
            this.panelHeight = '100%';
            let panelElementsHeight = this.socialPadding;
            if (this.header || this.splitHeader) {
                panelElementsHeight += this.headerHeight;
            }
            if (this.showFooter) {
                panelElementsHeight += this.footerHeight;
            }
        }
        if (this.fullWidth && !!this.title) {
            this.oldTitle = this.titleService.getTitle();
            this.titleService.setTitle(this.title);
        }
        window.addEventListener('resize', this.windowResizeHandler);
    }

    ngOnDestroy() {
        this.subscriptions.forEach(subscription => subscription.unsubscribe());
        this.navHeightObserver.disconnect();
        window.removeEventListener('resize', this.windowResizeHandler);
        this.close(true, true);
    }

    animationDone(event: AnimationEvent) {
        if (event.toState === OverlayAnimationStateEnum.close) {
            this.visibleChange.next(false);
        } else if (event.toState === OverlayAnimationStateEnum.open) {
            this.isPanelFullyOpened = true;
            this.finishedOpen.next(true);
        }
    }

    overlayAnimationStart(event: AnimationEvent) {
        if (this.fullWidth) {
            this.width = 100;
        }
        if (this.responsiveWidth && !this.width) {
            this.calculatePanelWidth();
        }
        if (event.toState === OverlayAnimationStateEnum.open) {
            this.slideInOutAnimation = {
                value: OverlayAnimationStateEnum.open,
                params: {
                    navBarHeight: this.coverNav ? '0px' : this.navBarHeight,
                    left: 100 - this.width
                }
            };
            requestAnimationFrame(() => this.showOverlay());
        }

        if (event.toState === OverlayAnimationStateEnum.close) {
            this.slideInOutAnimation = {
                value: OverlayAnimationStateEnum.close,
                params: {
                    navBarHeight: this.coverNav ? '0px' : this.navBarHeight,
                    left: 100 - this.width
                }
            };
            this.hideOverlay();
        }
    }

    close(changeTitle: boolean = true, ignoreValidation: boolean = false, event?: any) {
        if (event instanceof KeyboardEvent && !(event.key === ActionKeys.ENTER || event.key === ActionKeys.SPACE)
            || !this.isPanelFullyOpened) {
            return;
        }
        this.closeTakeover.emit(true);
        if (this.fullWidth && changeTitle && !!this.oldTitle) {
            this.titleService.setTitle(this.oldTitle);
        } else {
            this.oldTitle = null;
        }

        if (this.validateBeforeClose && !ignoreValidation) {
            this.validateBeforeClose.subscribe(
                done => {
                    if (done) {
                        this.overlayAnimation = OverlayAnimationStateEnum.close;
                        this.hideOverlay();
                    }
                }
            );
        } else {
            this.overlayAnimation = OverlayAnimationStateEnum.close;
            this.hideOverlay();
        }
    }

    clickOutside(event): void {
        if (!document.body.contains(event.srcElement)) {
            return;
        }
        if (event.srcElement.classList.contains(this.overlayClassName)) {
            this.closeTakeover.emit(true);
            this.close();
        }
    }

    showOverlay(): void {
        if (this.overlay || !this.overlayContainer) {
            return;
        }
        this.overlay = this.renderer.createElement('div');
        this.overlay.classList.add(this.overlayClassName);
        (this.overlay as HTMLElement).style.zIndex = DomHandler.zindex.toString();
        if (this.coverNav) {
            this.overlay.classList.add('fullHeight');
        } else {
            (this.overlay as HTMLElement).style.top = this.navBarHeight;
        }

        this.overlayContainer.appendChild(this.overlay);
        this.overlay.animate([
            {
                opacity: 0
            },
            {
                opacity: 0.5
            }
        ], 300);
    }

    hideOverlay(): void {
        if (this.overlay && this.done) {
            this.done = false;
            const animation = this.overlay.animate([
                {
                    opacity: 0.5
                },
                {
                    opacity: 0
                }
            ], 300);
            animation.finished.then(onfulfilled => {
                this.overlay.remove();
                this.overlay = null;
                this.done = true;
            });
        }
    }

    calculatePosition(navHeight: number) {
        this.navBarHeight = navHeight + 'px';
        this.panelHeight = (window.innerHeight - navHeight) + 'px';
        this.cd.detectChanges();
    }

    calculatePanelWidth() {
        const widthFactor = (this.maxPanelWidth - this.minPanelWidth)/(this.maxWindowWidth - this.minWindowWidth);
        // Difference between current window width and minimum window width, multiplied by width factor
        // plus minimum panel width gives width of panel in px
        let panelWidthPx = (window.innerWidth - this.minWindowWidth) * widthFactor + this.minPanelWidth;
        if (panelWidthPx > this.maxPanelWidth) {
            panelWidthPx = this.maxPanelWidth;
        } else if (panelWidthPx < this.minPanelWidth) {
            panelWidthPx = this.minPanelWidth;
        }
        // Then we calculate the %
        let panelWidth = panelWidthPx / window.innerWidth * 100;
        if (panelWidth > 100) {
            panelWidth = 100; // Maximum
        }
        this.width = panelWidth;
    }
}
